import {
  useBoundProp,
  useCardSession,
  useCardSessionControls,
  useCardStatus,
  type ComponentFn,
} from '@littlebird/action-cards-view/react';

import './Collapsible.css';
import { Icons } from '@/assets/icons/icons';
import { Checkbox } from '@/components/ui/Checkbox';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/Collapsible';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { cn } from '@/lib/utils';

import { StackedField } from '../field';
import { labelIdOf, useElementId } from '../fieldIds';
import {
  DETAILS_TRIGGER_CLASS,
  ICON_SM_CLASS,
  sizingClass,
  TEXT_STYLE,
} from '../tokens';

import type { catalog } from '@littlebird/action-cards-view';

type Item = {
  value: string;
  label: string;
  selected: boolean;
  group?: string;
};

const groupOf = (item: Item) => item.group ?? 'Other';

export const CheckList: ComponentFn<typeof catalog, 'CheckList'> = ({
  props,
  bindings,
}) => {
  const id = useElementId(props);
  const [items, setItems] = useBoundProp<Item[]>(props.value, bindings?.value);
  const session = useCardSession();
  const status = useCardStatus();
  const controls = useCardSessionControls();
  const path = bindings?.value;
  const held = status === 'running';
  // A literal value has no state path to write to, so its boxes cannot be ticked
  const editable =
    path !== undefined &&
    props.editable !== false &&
    !session.readOnly &&
    !held;
  const list = items ?? [];
  const grouped = list.some((item) => item.group !== undefined);
  const groups = [...new Set(list.map(groupOf))];

  const select = (picks: (entry: Item) => boolean, selected: boolean) => {
    setItems(
      list.map((entry) => (picks(entry) ? { ...entry, selected } : entry))
    );
    controls.commitNow();
  };

  const row = (item: Item) => {
    const itemId = `${id}-${item.value}`;
    return (
      <XStack
        key={item.value}
        className="min-w-0 items-center gap-[var(--ac-gap-sm)]"
      >
        <Checkbox
          id={itemId}
          checked={item.selected}
          disabled={!editable}
          onCheckedChange={(next) =>
            select((entry) => entry.value === item.value, next === true)
          }
        />
        <label
          htmlFor={itemId}
          className={cn(
            'min-w-0 break-words',
            TEXT_STYLE.body,
            item.selected ? 'text-foreground' : 'text-foreground-subtle'
          )}
        >
          {item.label}
        </label>
      </XStack>
    );
  };

  const groupSection = (group: string) => {
    const entries = list.filter((item) => groupOf(item) === group);
    const selectedCount = entries.filter((item) => item.selected).length;
    return (
      <Collapsible
        key={group}
        className="border-t border-border py-[var(--ac-gap-sm)] first:border-t-0"
      >
        <XStack className="min-w-0 items-center gap-[var(--ac-gap-sm)]">
          <Checkbox
            aria-label={`Select all ${group}`}
            checked={
              selectedCount === entries.length
                ? true
                : selectedCount === 0
                  ? false
                  : 'indeterminate'
            }
            disabled={!editable}
            onCheckedChange={(next) =>
              select((entry) => groupOf(entry) === group, next === true)
            }
          />
          <CollapsibleTrigger className={DETAILS_TRIGGER_CLASS}>
            <Icons.chevronRight
              aria-hidden="true"
              className={cn(
                ICON_SM_CLASS,
                'shrink-0 transition-transform duration-[120ms] group-data-[state=open]:rotate-90 motion-reduce:duration-[60ms]'
              )}
            />
            <span className="min-w-0 truncate">{group}</span>{' '}
            <span className="shrink-0 tabular-nums">{entries.length}</span>
          </CollapsibleTrigger>
        </XStack>
        <CollapsibleContent className="card-disclosure">
          <YStack className="gap-[var(--ac-gap-xs)] pt-[var(--ac-gap-sm)] pl-7">
            {entries.map(row)}
          </YStack>
        </CollapsibleContent>
      </Collapsible>
    );
  };

  const field = (
    <StackedField
      id={id}
      label={props.label}
      labelsControl={false}
      error={undefined}
    >
      <YStack
        id={id}
        role="group"
        aria-labelledby={props.label ? labelIdOf(id) : undefined}
        className="gap-[var(--ac-gap-xs)] py-[calc(2*var(--ac-u))]"
      >
        {grouped ? groups.map(groupSection) : list.map(row)}
      </YStack>
    </StackedField>
  );

  return props.sizing === 'fill' ? (
    <div className={sizingClass(props.sizing)}>{field}</div>
  ) : (
    field
  );
};
